import { createSelector } from 'reselect';
import { reduxKey } from '../constants';
import type { BoostInitializationStoreSlice } from '../types';
import { getDenormalizedDataEntityById } from '../../../../../../../redux/entities/selectors';
import { deviationSchema } from '../../../../../../../redux/normalizr/schemas/deviation';
import type { PapiDeviation } from '@wix/da-papi-types';

const getBoostState = (state: any): BoostInitializationStoreSlice =>
  state[reduxKey] || {
    requestStatus: 'idle',
    requestError: undefined,
    isInitialized: false,
    deviationId: undefined,
    tags: [],
    recommendedTags: [],
    // Boost submission state
    boostStatus: 'idle',
    boostError: undefined,
    isSubmitting: false,
  };

export const getRecommendedTags = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): string[] => {
    return boostState.recommendedTags;
  }
);

export const getTags = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): string[] => {
    return boostState.tags;
  }
);

export const getCurrentDeviationId = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): number | undefined => {
    return boostState.deviationId;
  }
);

export const getBoostRestrictions = createSelector(
  [getBoostState, (state: any) => state],
  (boostState: BoostInitializationStoreSlice, state: any) => {
    if (!boostState.deviationId) {
      return {
        canUseFreeBoost: false,
        canUsePaidBoost: false,
        freeBoostsAllowed: 0,
        freeBoostsRemaining: 0,
        boostPointsPrice: 250,
        pointsBalance: 0,
      };
    }

    const deviation: PapiDeviation | undefined = getDenormalizedDataEntityById(
      state,
      deviationSchema,
      boostState.deviationId
    );

    return {
      canUseFreeBoost:
        deviation?.extended?.boostRestrictions?.canUseFreeBoost || false,
      canUsePaidBoost:
        deviation?.extended?.boostRestrictions?.canUsePaidBoost || false,
      freeBoostsAllowed:
        deviation?.extended?.boostRestrictions?.freeBoostsAllowed || 0,
      freeBoostsRemaining:
        deviation?.extended?.boostRestrictions?.freeBoostsRemaining || 0,
      boostPointsPrice:
        deviation?.extended?.boostRestrictions?.boostPointsPrice || 250,
      pointsBalance: deviation?.extended?.boostRestrictions?.pointsBalance || 0,
    };
  }
);

export const isBoostDataLoading = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): boolean => {
    return boostState.requestStatus === 'fetching';
  }
);

export const getBoostDataError = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): string | undefined => {
    return boostState.requestError || undefined;
  }
);

export const isBoostDataInitialized = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): boolean => {
    return boostState.isInitialized;
  }
);

// Boost submission selectors
export const isBoostSubmitting = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): boolean => {
    return boostState.isSubmitting;
  }
);

export const getBoostSubmissionError = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): string | undefined => {
    return boostState.boostError || undefined;
  }
);

export const isBoostSubmissionSuccess = createSelector(
  [getBoostState],
  (boostState: BoostInitializationStoreSlice): boolean => {
    return boostState.boostStatus === 'success';
  }
);
